import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  obtenerReaperturasPendientes,
  aprobarReaperturaSinTrazabilidad,
  rechazarReaperturaSinTrazabilidad,
} from "../../services/pruebasService";

interface ReaperturaPendiente {
  id: string;
  pedido_id: string;
  solicitado_por: string;
  fecha_emision_nueva: string;
  fecha_entrega_nueva: string;
  created_at: string;
  pedidos: { origen_numero: string | null; nombre_cliente: string | null } | null;
}

interface ReaperturasPendientesPanelProps {
  userName: string;
  onRefreshBackend?: () => void;
}

export function ReaperturasPendientesPanel({ userName, onRefreshBackend }: ReaperturasPendientesPanelProps) {
  const [pendientes, setPendientes] = useState<ReaperturaPendiente[]>([]);
  const [procesandoId, setProcesandoId] = useState<string | null>(null);

  const refresh = () => {
    obtenerReaperturasPendientes()
      .then(setPendientes)
      .catch(err => console.error("Error al cargar reaperturas pendientes:", err));
  };

  useEffect(() => {
    refresh();
  }, []);

  if (pendientes.length === 0) return null;

  const handleAprobar = async (id: string) => {
    setProcesandoId(id);
    try {
      await aprobarReaperturaSinTrazabilidad(id, userName);
      toast.success("Reapertura aprobada. El pedido volvió a estar abierto.");
      refresh();
      onRefreshBackend?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Error al aprobar: ${msg}`);
    } finally {
      setProcesandoId(null);
    }
  };

  const handleRechazar = async (id: string) => {
    setProcesandoId(id);
    try {
      await rechazarReaperturaSinTrazabilidad(id);
      toast.success("Solicitud de reapertura rechazada.");
      refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Error al rechazar: ${msg}`);
    } finally {
      setProcesandoId(null);
    }
  };

  return (
    <Card className="mb-4 border-orange-400">
      <CardHeader>
        <CardTitle className="text-orange-700">Reaperturas pendientes de autorización</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {pendientes.map(r => (
          <div key={r.id} className="flex items-center justify-between gap-4 border rounded-md p-3">
            <div className="text-sm">
              <p className="font-medium">
                {r.pedidos?.origen_numero || r.pedido_id} — {r.pedidos?.nombre_cliente || "Sin cliente"}
              </p>
              <p className="text-muted-foreground">
                Solicitado por {r.solicitado_por} el {new Date(r.created_at).toLocaleDateString('es-MX')}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                disabled={procesandoId === r.id}
                onClick={() => handleRechazar(r.id)}
              >
                Rechazar
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    size="sm"
                    className="!bg-orange-600 !text-white hover:!bg-orange-700"
                    disabled={procesandoId === r.id}
                  >
                    Aprobar
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Aprobar reapertura sin trazabilidad?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esto borrará los informes y el historial de edición de este pedido, y lo
                      regresará a borrador. Esta acción no se puede deshacer.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleAprobar(r.id)}>
                      Sí, aprobar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
